import { Weight, WeightType } from '@hero/schema';

export const convertWeight = (
  weight: Weight,
  target: WeightType,
  options?: { decimals?: number; removeTrailingZeros?: boolean },
): Weight => {
  type WeightConversion = {
    readonly [key in WeightType]: {
      readonly [key in WeightType]: number;
    };
  };
  const conversion: Readonly<WeightConversion> = {
    gram: {
      gram: 1,
      kg: 0.001,
      ton: 0.000001,
    },
    kg: {
      kg: 1,
      gram: 1000,
      ton: 0.001,
    },
    ton: {
      kg: 1000,
      gram: 1000000,
      ton: 1,
    },
  };
  const targetRate = conversion[weight.type][target];
  let value = targetRate * weight.value;

  // Apply rounding if decimals option is provided
  const decimals = options?.decimals ?? 3;
  if (decimals >= 0) {
    value = parseFloat(value.toFixed(decimals));
    // Remove trailing zeros if requested
    if (options?.removeTrailingZeros) {
      value = parseFloat(value.toString());
    }
  }

  return {
    type: target,
    value,
  };
};

const formatWeightNumber = (value: number): string => {
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
};

/**
 * Human-readable weight for documents/UI: tons when ≥ 1 ton (one decimal),
 * otherwise kg. English unit labels only (e.g. "24 tons", "24.5 tons", "500 kg").
 */
export const formatWeightForDisplay = (weight: Weight): string => {
  if (!(weight.value > 0)) {
    return '';
  }

  const kg = convertWeight(weight, 'kg', { decimals: 3, removeTrailingZeros: true });
  if (kg.value >= 1000) {
    const tons = convertWeight(weight, 'ton', { decimals: 1, removeTrailingZeros: true });
    const unit = tons.value === 1 ? 'ton' : 'tons';
    return `${formatWeightNumber(tons.value)} ${unit}`;
  }

  return `${formatWeightNumber(kg.value)} kg`;
};
